import { createTheme, PaletteMode } from '@mui/material/styles';

interface ThemeOptions {
  primary?: string;
  secondary?: string;
  fontFamily?: string;
}

export const createAppTheme = (mode: PaletteMode, options?: ThemeOptions) => {
  const primaryColor = options?.primary || '#0c267c';
  const secondaryColor = options?.secondary || '#f50057';
  const fontFamily = options?.fontFamily || '"Inter", "Roboto", sans-serif';

  return createTheme({
    palette: {
      mode,
      ...(mode === 'light'
        ? {
            primary: {
              main: primaryColor
            },
            secondary: {
              main: secondaryColor
            },
            background: {
              default: '#f5f5f5',
              paper: '#ffffff'
            },
            text: {
              primary: '#333333',
              secondary: '#666666'
            },
            navigation: {
              background: '#ffffff',
              text: '#333333',
              activeBackground: `rgba(${hexToRgb(primaryColor)}, 0.08)`,
              activeText: primaryColor,
              hoverBackground: `rgba(${hexToRgb(primaryColor)}, 0.04)`,
              borderColor: 'rgba(0, 0, 0, 0.12)'
            }
          }
        : {
            primary: {
              main: '#ffffff'
            },
            secondary: {
              main: lightenColor(secondaryColor, 20)
            },
            background: {
              default: '#121212',
              paper: '#1e1e1e'
            },
            text: {
              primary: '#ffffff',
              secondary: '#b0b0b0'
            },
            navigation: {
              background: '#1e1e1e',
              text: '#ffffff',
              activeBackground: `rgba(255, 255, 255, 0.16)`,
              activeText: '#ffffff',
              hoverBackground: 'rgba(255, 255, 255, 0.08)',
              borderColor: 'rgba(255, 255, 255, 0.12)'
            }
          })
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          '*': {
            scrollBehavior: 'smooth !important',
            scrollbarWidth: 'thin',
            scrollbarColor: mode === 'light' 
              ? 'rgba(0, 0, 0, 0.1) transparent' 
              : 'rgba(255, 255, 255, 0.1) transparent',
          },
          'html, body': {
            scrollBehavior: 'smooth !important',
            WebkitOverflowScrolling: 'touch',
          },
          '*::-webkit-scrollbar': {
            width: '0px',
            height: '0px',
            backgroundColor: 'transparent',
            transition: 'width 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          },
          '*::-webkit-scrollbar-track': {
            background: 'transparent',
            borderRadius: '6px',
          },
          '*::-webkit-scrollbar-thumb': {
            background: 'transparent',
            borderRadius: '6px',
            transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          },
          '*:hover::-webkit-scrollbar': {
            width: '12px',
            height: '12px',
          },
          '*:hover::-webkit-scrollbar-thumb': {
            background: mode === 'light' 
              ? 'rgba(0, 0, 0, 0.15)' 
              : 'rgba(255, 255, 255, 0.15)',
          },
          '*::-webkit-scrollbar-thumb:hover': {
            background: mode === 'light' 
              ? 'rgba(0, 0, 0, 0.25)' 
              : 'rgba(255, 255, 255, 0.25)',
          },
          'div, section, main, aside, article, nav, header, footer': {
            scrollBehavior: 'smooth !important',
            WebkitOverflowScrolling: 'touch',
          },
          'div[style*="overflow"], div[style*="scroll"]': {
            scrollBehavior: 'smooth !important',
            WebkitOverflowScrolling: 'touch',
          },
          '.MuiBox-root, .MuiContainer-root, .MuiPaper-root, .MuiDrawer-paper, .MuiDialog-paper, .MuiMenu-paper, .MuiPopover-paper': {
            scrollBehavior: 'smooth !important',
            WebkitOverflowScrolling: 'touch',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: mode === 'light' ? '#ffffff' : '#1e1e1e',
            color: mode === 'light' ? '#333333' : '#ffffff',
            borderBottom: `1px solid ${mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)'}`
          }
        }
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: mode === 'light' ? '#ffffff' : '#1e1e1e',
            color: mode === 'light' ? '#333333' : '#ffffff',
            borderRight: `1px solid ${mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)'}`
          }
        }
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: mode === 'light' ? '#ffffff' : '#2c2c2c',
            color: mode === 'light' ? '#333333' : '#ffffff',
            boxShadow: mode === 'light' ? '0 4px 6px rgba(0,0,0,0.1)' : '0 4px 6px rgba(255,255,255,0.1)'
          }
        }
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: mode === 'light' ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.08)'
            },
            '&.Mui-selected': {
              backgroundColor: mode === 'light' ? 'rgba(25, 118, 210, 0.08)' : 'rgba(144, 202, 249, 0.16)'
            }
          }
        }
      }
    },
   typography: {
  fontFamily: '"Source Sans Pro", "Segoe UI", "Helvetica Neue", sans-serif'}

  });
};

function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '0, 0, 0';
}

function lightenColor(hex: string, percent: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = ((num >> 8) & 0x00ff) + amt;
  const B = (num & 0x0000ff) + amt;
  return (
    '#' +
    (0x1000000 + (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 + (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 + (B < 255 ? (B < 1 ? 0 : B) : 255))
      .toString(16)
      .slice(1)
  );
}

declare module '@mui/material/styles' {
  interface Palette {
    navigation: {
      background: string;
      text: string;
      activeBackground: string;
      activeText: string;
      hoverBackground: string;
      borderColor: string;
    };
  }
  interface PaletteOptions {
    navigation?: {
      background?: string;
      text?: string;
      activeBackground?: string;
      activeText?: string;
      hoverBackground?: string;
      borderColor?: string;
    };
  }
}